import { useEffect } from 'react';

function prefersReducedMotion() {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function splitWords(el) {
  if (!el || el.dataset.split === '1') return;
  const text = el.textContent || '';
  const parts = text.split(/(\s+)/);
  el.setAttribute('aria-label', text.trim());
  el.innerHTML = parts
    .map((part) => {
      if (/^\s+$/.test(part)) return part;
      if (!part) return '';
      return `<span class="home-reveal__word" aria-hidden="true"><span class="home-reveal__word-inner">${part}</span></span>`;
    })
    .join('');
  el.dataset.split = '1';
}

function finishReveal(el) {
  if (!el) return;
  el.classList.add('is-revealed');
  el.style.animation = 'none';
  el.style.setProperty('opacity', '1', 'important');
  el.style.setProperty('transform', 'none', 'important');
}

function revealEl(el, delayMs = 0) {
  if (!el || el.classList.contains('is-revealed')) return;
  if (delayMs > 0) el.style.setProperty('--reveal-delay', `${delayMs}ms`);
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      el.classList.add('is-revealed');
      window.setTimeout(() => {
        if (Number(getComputedStyle(el).opacity) < 0.95) finishReveal(el);
      }, delayMs + 1100);
    });
  });
}

function isRevealTarget(el) {
  if (!(el instanceof Element)) return false;
  return (
    el.hasAttribute('data-reveal-item')
    || el.getAttribute('data-reveal') === 'fade'
    || el.getAttribute('data-reveal') === 'section'
    || el.classList.contains('home-reveal__word-inner')
  );
}

export function useHomeReveal(rootRef) {
  useEffect(() => {
    const root = rootRef?.current;
    if (!root) return undefined;

    root.classList.add('home--reveal-ready');

    root.querySelectorAll('[data-reveal-words]').forEach(splitWords);

    const wordInners = [...root.querySelectorAll('.home-reveal__word-inner')];
    const fadeEls = [...root.querySelectorAll('[data-reveal="fade"]')];
    const heroItems = [...root.querySelectorAll('[data-reveal="hero"] [data-reveal-item]')];
    const sectionNodes = [...root.querySelectorAll('[data-reveal="section"]')];

    if (prefersReducedMotion()) {
      [...heroItems, ...fadeEls, ...wordInners, ...sectionNodes.flatMap((s) => [...s.querySelectorAll('[data-reveal-item]')])]
        .forEach((el) => el.classList.add('is-revealed'));
      return undefined;
    }

    heroItems.forEach((el, i) => revealEl(el, 60 + i * 110));

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const target = entry.target;

          if (target.hasAttribute('data-reveal-words')) {
            const words = [...target.querySelectorAll('.home-reveal__word-inner')];
            words.forEach((w, i) => revealEl(w, i * 55));
            io.unobserve(target);
            return;
          }

          if (target.getAttribute('data-reveal') === 'fade') {
            revealEl(target, 0);
            io.unobserve(target);
            return;
          }

          if (target.getAttribute('data-reveal') === 'section') {
            const items = [...target.querySelectorAll('[data-reveal-item]')].filter(
              (el) => el.closest('[data-reveal="section"]') === target,
            );
            const list = items.length ? items : [target];
            list.forEach((el, i) => revealEl(el, i * 90));
            io.unobserve(target);
          }
        });
      },
      { root: null, rootMargin: '0px 0px -8% 0px', threshold: 0.12 },
    );

    root.querySelectorAll('[data-reveal-words], [data-reveal="fade"], [data-reveal="section"]').forEach((el) => {
      io.observe(el);
    });

    const revealLateNode = (el) => {
      if (!isRevealTarget(el) || el.classList.contains('is-revealed')) return;
      if (el.closest('[data-reveal="hero"]') && el.hasAttribute('data-reveal-item')) {
        finishReveal(el);
        return;
      }
      if (el.getAttribute('data-reveal') === 'section') {
        io.observe(el);
        return;
      }
      if (el.hasAttribute('data-reveal-item')) {
        const section = el.closest('[data-reveal="section"]');
        if (section) {
          io.observe(section);
          return;
        }
        finishReveal(el);
      }
    };

    const mo = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          revealLateNode(node);
          node.querySelectorAll?.('[data-reveal-item], [data-reveal="section"], [data-reveal="fade"], .home-reveal__word-inner').forEach(revealLateNode);
        });
      }
    });
    mo.observe(root, { childList: true, subtree: true });

    const safety = window.setTimeout(() => {
      root.querySelectorAll(
        '[data-reveal-item], [data-reveal="fade"], .home-reveal__word-inner',
      ).forEach((el) => {
        if (Number(getComputedStyle(el).opacity) < 0.95) finishReveal(el);
      });
    }, 1600);

    return () => {
      io.disconnect();
      mo.disconnect();
      window.clearTimeout(safety);
    };
  }, [rootRef]);
}
